import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';

const STATUS_STYLES = {
    active: 'bg-green-100 text-green-700 border-green-200',
    paused: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    cancelled: 'bg-red-100 text-red-700 border-red-200',
    expired: 'bg-gray-100 text-gray-600 border-gray-200',
    pending_payment: 'bg-blue-100 text-blue-700 border-blue-200',
};

const SubscriptionManagement = () => {
    const [activeTab, setActiveTab] = useState('todaysOrders');
    const [todaysOrders, setTodaysOrders] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [deliveryPartners, setDeliveryPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState({});
    const [frequencies, setFrequencies] = useState([]);
    const [savingFrequencies, setSavingFrequencies] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const [ordersRes, subsRes, partnersRes, settingsRes] = await Promise.all([
                apiClient.get('/subscriptions/todays-orders', { headers }),
                apiClient.get('/subscriptions', { headers }),
                apiClient.get('/users/delivery-partners', { headers }),
                apiClient.get('/settings')
            ]);
            setTodaysOrders(ordersRes.data);
            setSubscriptions(subsRes.data);
            setDeliveryPartners(partnersRes.data);
            setFrequencies(settingsRes.data?.subscriptionFrequencies || [1, 2, 7]);
        } catch (error) {
            console.error('Failed to load subscription data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAssign = async (orderId) => {
        const partnerId = selectedPartner[orderId];
        if (!partnerId) {
            alert('Please select a delivery partner.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await apiClient.post('/delivery/assign', { orderId, deliveryPersonId: partnerId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Rider assigned!');
            fetchAll();
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to assign rider.');
        }
    };

    const toggleFrequency = (day) => {
        setFrequencies(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b));
    };

    const saveFrequencies = async () => {
        setSavingFrequencies(true);
        try {
            const token = localStorage.getItem('token');
            await apiClient.put('/settings', { subscriptionFrequencies: frequencies }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Subscription frequencies updated!');
        } catch (error) {
            alert('Failed to save frequencies.');
        } finally {
            setSavingFrequencies(false);
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="p-4 md:p-6 bg-white shadow-md rounded-lg max-w-full overflow-hidden">
            <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-8 w-2 bg-indigo-500 rounded-full"></div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight text-center">Subscribe &amp; Save</h2>
            </div>

            <div className="border-b border-gray-200 mb-6 flex gap-4">
                <button onClick={() => setActiveTab('todaysOrders')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'todaysOrders' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500'}`}>Today's Orders</button>
                <button onClick={() => setActiveTab('allSubscriptions')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'allSubscriptions' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500'}`}>All Subscriptions</button>
                <button onClick={() => setActiveTab('frequencies')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'frequencies' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500'}`}>Frequencies</button>
            </div>

            {activeTab === 'todaysOrders' && (
                <div className="space-y-4">
                    {todaysOrders.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No subscription orders generated today yet.</p>
                    ) : (
                        todaysOrders.map(order => (
                            <div key={order._id} className="bg-gray-50 rounded-xl border border-gray-200 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-bold text-gray-900">{order.orderNumber}</p>
                                    <p className="text-xs text-gray-500">{order.user?.name} — {order.shippingAddress?.address}, {order.shippingAddress?.city} - {order.shippingAddress?.postalCode}</p>
                                    <p className="text-xs text-gray-600 mt-1">{order.orderItems?.map(i => `${i.name} x${i.qty}`).join(', ')}</p>
                                </div>
                                {order.assignedTo ? (
                                    <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200 w-fit">
                                        Assigned: {order.assignedTo.name}
                                    </span>
                                ) : (
                                    <div className="flex gap-2 items-center">
                                        <select
                                            value={selectedPartner[order._id] || ''}
                                            onChange={(e) => setSelectedPartner(prev => ({ ...prev, [order._id]: e.target.value }))}
                                            className="p-2 border rounded text-sm"
                                        >
                                            <option value="">Select rider</option>
                                            {deliveryPartners.map(p => (
                                                <option key={p._id} value={p._id}>{p.name}</option>
                                            ))}
                                        </select>
                                        <button onClick={() => handleAssign(order._id)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700">
                                            Assign
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'allSubscriptions' && (
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-100 border-b">
                                <th className="p-3 font-semibold">Customer</th>
                                <th className="p-3 font-semibold">Product</th>
                                <th className="p-3 font-semibold">Frequency</th>
                                <th className="p-3 font-semibold">Progress</th>
                                <th className="p-3 font-semibold">Rider</th>
                                <th className="p-3 font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subscriptions.map(s => (
                                <tr key={s._id} className="border-b hover:bg-gray-50">
                                    <td className="p-3 text-sm">{s.user?.name}<br /><span className="text-xs text-gray-500">{s.user?.phone}</span></td>
                                    <td className="p-3 text-sm">{s.product?.name} ({s.size})</td>
                                    <td className="p-3 text-sm">Every {s.intervalDays} day(s)</td>
                                    <td className="p-3 text-sm">{s.cyclesDelivered}/{s.totalCycles}</td>
                                    <td className="p-3 text-sm">{s.assignedTo?.name || '—'}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded text-xs font-bold border ${STATUS_STYLES[s.status] || ''}`}>
                                            {s.status.replace('_', ' ').toUpperCase()}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {subscriptions.length === 0 && (
                                <tr><td colSpan="6" className="p-4 text-center text-gray-500">No subscriptions yet</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {activeTab === 'frequencies' && (
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 max-w-md">
                    <h3 className="text-lg font-bold text-gray-700 mb-4">Available Delivery Frequencies</h3>
                    <div className="flex flex-wrap gap-2 mb-6">
                        {[1, 2, 3, 7, 14, 30].map(day => (
                            <button
                                key={day}
                                type="button"
                                onClick={() => toggleFrequency(day)}
                                className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 ${frequencies.includes(day) ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500'}`}
                            >
                                Every {day} day{day > 1 ? 's' : ''}
                            </button>
                        ))}
                    </div>
                    <button onClick={saveFrequencies} disabled={savingFrequencies} className="bg-indigo-600 text-white font-bold px-6 py-2.5 rounded-lg hover:bg-indigo-700 disabled:opacity-60">
                        {savingFrequencies ? 'Saving...' : 'Save Frequencies'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default SubscriptionManagement;
